import { useEffect, useLayoutEffect, useRef, useState, type SyntheticEvent } from 'react';
import { deplacer } from './clavier.js';
import { Arbre } from './dispositions.js';
import type { Affichage } from './noeud.js';
import { ordreDeLecture } from './clavier.js';
import type { DonneesArbre, IdDisposition, StatutContact } from './types.js';

export interface PropsArbreNavigable {
  donnees: DonneesArbre;
  disposition: IdDisposition | 'dessine';
  focusId: string;
  heritierId?: string;
  vue?: 'equipe' | 'famille';
  afficher?: Affichage;
  statuts?: Readonly<Record<string, StatutContact>>;
  viewerId?: string;
  onChangerFocus: (id: string) => void;
  onOuvrir: (id: string) => void;
  /** Équipe : bouton « Ajouter un frère ou une sœur » sur chaque carte de l'arbre dessiné (ADR 0064). */
  onAjouterFratrie?: (id: string) => void;
}

interface EvenementClavier {
  key: string;
  preventDefault: () => void;
  currentTarget?: Element | null;
}

/** Identifiants des nœuds réellement affichés par la disposition courante, dans l'ordre du document. */
const idsAffiches = (arbre: Element | null | undefined): string[] | undefined =>
  arbre ? [...arbre.querySelectorAll<HTMLElement>('[data-noeud]')].map((n) => n.dataset['noeud'] ?? '') : undefined;

/**
 * Suivi du focus : garde un seul arrêt de tabulation sur un nœud affiché (la disposition « chemin » n'en montre qu'une
 * partie) et pose le focus du DOM sur le nœud sélectionné quand il change au clavier, pour que le lecteur d'écran
 * annonce le bon nœud. N'arrache jamais le focus à un élément extérieur à l'arbre.
 */
function SuiviFocus({ focusId, onChangerFocus }: { focusId: string; onChangerFocus: (id: string) => void }) {
  const repere = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    const ids = idsAffiches(repere.current?.parentElement);
    const premier = ids?.[0];
    if (premier !== undefined && !ids?.includes(focusId)) onChangerFocus(premier);
  });
  useEffect(() => {
    const arbre = repere.current?.parentElement;
    const actif = document.activeElement;
    if (!arbre || !actif || !arbre.contains(actif)) return;
    const cible = [...arbre.querySelectorAll<HTMLElement>('[data-noeud]')].find((n) => n.dataset['noeud'] === focusId);
    if (cible && cible !== actif) cible.focus();
  }, [focusId]);
  return <span ref={repere} hidden aria-hidden="true" />;
}

/**
 * Arbre au clavier (modèle « roving tabindex ») : Tab entre dans l'arbre sur un seul élément, les flèches parcourent
 * la famille, Entrée ou Espace ouvre la personne. Sans état propre : le focus est tenu par le parent.
 */
export function ArbreNavigable(props: PropsArbreNavigable) {
  const { donnees, focusId, onChangerFocus, onOuvrir, ...reste } = props;
  const surTouche = (e: EvenementClavier): void => {
    const r = deplacer(donnees, focusId, e.key, idsAffiches(e.currentTarget));
    if (r.ignoree) return;
    e.preventDefault();
    if (r.focusId !== focusId) onChangerFocus(r.focusId);
    if (r.ouvrir) onOuvrir(r.ouvrir);
  };
  const surFocus = (e: SyntheticEvent): void => {
    const id = (e.target as HTMLElement).closest?.('[data-noeud]')?.getAttribute('data-noeud');
    if (id && id !== focusId) onChangerFocus(id);
  };
  return (
    <div role="tree" aria-label="Arbre généalogique" onKeyDown={surTouche} onFocus={surFocus}>
      <Arbre donnees={donnees} focusId={focusId} {...reste} />
      <SuiviFocus focusId={focusId} onChangerFocus={onChangerFocus} />
    </div>
  );
}

/** Version avec état : le focus démarre sur la première personne et suit les flèches (et les clics). */
export function ArbreInteractif(
  props: Omit<PropsArbreNavigable, 'focusId' | 'onChangerFocus'> & { onChangerFocus?: (id: string) => void },
) {
  const premier = ordreDeLecture(props.donnees)[0] ?? '';
  const [focusId, setFocusId] = useState<string>(premier);
  const { onChangerFocus, ...reste } = props;
  const changer = (id: string): void => {
    setFocusId(id);
    onChangerFocus?.(id);
  };
  return <ArbreNavigable {...reste} focusId={focusId} onChangerFocus={changer} />;
}
